import React from "react";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import StatusBadge from "components/ui/StatusBadge";
import { authService } from "services/authService";
import { participantInsightsService } from "services/admin/participantInsightsService";
import { userService } from "services/admin/userService";
import { teachingClassService } from "services/userTeaching/teachingClassService";
import type {
  ClassSession,
  PaginatedResponse,
  UserListItem,
} from "types/domain";
import type { ParticipantSessionRow } from "services/admin/participantInsightsService";
import { MdFileDownload } from "react-icons/md";
import { useLanguage } from "context/LanguageContext";
import ExportHistoryModal from "./components/ExportHistoryModal";

const statusBadgeKey = (status: ClassSession["status"]) => {
  if (status === "ACTIVE") return "enum.sessionStatus.ACTIVE";
  if (status === "COMPLETED" || status === "CLOSED")
    return "enum.sessionStatus.COMPLETED";
  if (status === "CANCELLED") return "enum.sessionStatus.CANCELLED";
  return "enum.sessionStatus.SCHEDULED";
};

const statusBadgeTone = (
  status: ClassSession["status"]
): "scheduled" | "neutral" | "danger" | "info" => {
  if (status === "ACTIVE") return "scheduled";
  if (status === "COMPLETED" || status === "CLOSED") return "neutral";
  if (status === "CANCELLED") return "danger";
  return "info";
};

const formatDateTime = (value: string, locale: string) =>
  new Date(value).toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function getCurrentMonth(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 7);
}

const getMonthRange = (month: string) => {
  const [year, m] = month.split("-").map(Number);
  const from = `${month}-01`;
  const lastDay = new Date(year, m, 0).getDate();
  const to = `${month}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
};

const PAGE_SIZE = 50;

type SessionLookupRow = Pick<
  ClassSession,
  | "id"
  | "title"
  | "class_name"
  | "start_time"
  | "end_time"
  | "status"
  | "location"
>;

const UserSessions = () => {
  const { t, locale } = useLanguage();
  const currentUser = React.useMemo(() => authService.getStoredUser(), []);
  const currentRole = (currentUser?.role ?? "").toUpperCase();
  const isTrainerOrTeacher =
    currentRole === "TRAINER" || currentRole === "TEACHER";
  const currentMonth = React.useMemo(() => getCurrentMonth(), []);
  const [selectedMonth, setSelectedMonth] = React.useState(currentMonth);
  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [userFilter, setUserFilter] = React.useState("");
  const [allUsers, setAllUsers] = React.useState<UserListItem[]>([]);
  const [trainerParticipantRows, setTrainerParticipantRows] = React.useState<
    ParticipantSessionRow[]
  >([]);
  const [usersLoading, setUsersLoading] = React.useState(true);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const [sessions, setSessions] = React.useState<SessionLookupRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [skip, setSkip] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [searched, setSearched] = React.useState(false);

  // Load all users on mount
  React.useEffect(() => {
    let active = true;
    const monthRange = getMonthRange(selectedMonth);

    if (isTrainerOrTeacher) {
      (async () => {
        try {
          const [assignedClasses, participantRows] = await Promise.all([
            teachingClassService.getMyAssignedClasses(),
            participantInsightsService.getParticipantRows({
              fromDate: monthRange.from,
              toDate: monthRange.to,
              roles: ["student"],
              t,
            }),
          ]);

          if (!active) return;

          const studentsMap = new Map<string, UserListItem>();
          assignedClasses.forEach((classItem) => {
            classItem.enrollments.forEach((enrollment) => {
              if (!enrollment.student) return;
              studentsMap.set(enrollment.student.id, enrollment.student);
            });
          });

          setAllUsers(Array.from(studentsMap.values()));
          setTrainerParticipantRows(participantRows);
        } catch {
          if (active) {
            setError("userSessions.loadAssignedError");
            setAllUsers([]);
            setTrainerParticipantRows([]);
          }
        } finally {
          if (active) setUsersLoading(false);
        }
      })();

      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        let items: UserListItem[] = [];
        let skip = 0;
        const limit = 200;
        while (true) {
          const res = await userService.getAll({
            skip,
            limit,
            role: "STUDENT",
          });
          items = [...items, ...res.items];
          if (res.items.length < limit) break;
          skip += limit;
        }
        if (active) setAllUsers(items);
      } catch {
        // ignore
      } finally {
        if (active) setUsersLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedMonth, isTrainerOrTeacher, t]);

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredUsers = React.useMemo(() => {
    if (!userFilter.trim()) return allUsers;
    const q = userFilter.toLowerCase();
    return allUsers.filter(
      (u) =>
        (u.full_name ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.code ?? "").toLowerCase().includes(q)
    );
  }, [allUsers, userFilter]);

  const fetchSessions = React.useCallback(
    (currentSkip: number) => {
      if (!selectedUserId || !selectedMonth) return;
      const monthRange = getMonthRange(selectedMonth);
      setLoading(true);
      setError(null);

      if (isTrainerOrTeacher) {
        const filtered = trainerParticipantRows.filter(
          (row) => row.userId === selectedUserId
        );
        const sessionMap = new Map<string, SessionLookupRow>();
        filtered.forEach((row) => {
          if (sessionMap.has(row.sessionId)) return;
          sessionMap.set(row.sessionId, {
            id: row.sessionId,
            title: row.sessionTitle,
            class_name: row.className,
            start_time: row.startTime,
            end_time: row.endTime,
            status: row.sessionStatus,
            location: null,
          });
        });
        const rows = Array.from(sessionMap.values()).sort((a, b) => {
          const left = a.start_time ? new Date(a.start_time).getTime() : 0;
          const right = b.start_time ? new Date(b.start_time).getTime() : 0;
          return right - left;
        });
        const paged = rows.slice(currentSkip, currentSkip + PAGE_SIZE);
        setSessions(paged);
        setTotal(rows.length);
        setSkip(currentSkip);
        setSearched(true);
        setLoading(false);
        return;
      }

      userService
        .getUserSessions(
          selectedUserId,
          monthRange.from,
          monthRange.to,
          currentSkip,
          PAGE_SIZE
        )
        .then((res: PaginatedResponse<ClassSession>) => {
          setSessions(res.items);
          setTotal(res.total);
          setSkip(currentSkip);
          setSearched(true);
        })
        .catch((err: Error) => setError(err.message))
        .finally(() => setLoading(false));
    },
    [selectedMonth, isTrainerOrTeacher, selectedUserId, trainerParticipantRows]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSkip(0);
    fetchSessions(0);
  };

  const selectedUser = React.useMemo(
    () => allUsers.find((u) => u.id === selectedUserId) ?? null,
    [allUsers, selectedUserId]
  );

  return (
    <div className="mt-5 flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-ink">
          {t("nav.userSessions")}
        </h2>
        <ActionButton
          variant="secondary"
          onClick={() => setExportOpen(true)}
          startIcon={<MdFileDownload className="h-4 w-4" />}
        >
          {t("exportHistory.openButton")}
        </ActionButton>
      </div>

      <Card extra="w-full p-5">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* User search */}
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("userSessions.user")}
            </label>
            <div className="relative" ref={dropdownRef}>
              <input
                type="text"
                placeholder={
                  usersLoading
                    ? t("userSessions.usersLoading")
                    : t("exportHistory.searchPlaceholder")
                }
                value={
                  selectedUserId
                    ? selectedUser
                      ? `${
                          selectedUser.full_name ??
                          selectedUser.email ??
                          selectedUser.code ??
                          t("userSessions.user")
                        }${
                          selectedUser.email ? ` (${selectedUser.email})` : ""
                        }`
                      : userFilter
                    : userFilter
                }
                onFocus={() => setShowDropdown(true)}
                onChange={(e) => {
                  setUserFilter(e.target.value);
                  setSelectedUserId("");
                  setShowDropdown(true);
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-surface-muted dark:text-white"
              />
              {selectedUserId && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUserId("");
                    setUserFilter("");
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-gray-600"
                >
                  ✕
                </button>
              )}
              {showDropdown && !selectedUserId && (
                <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-line bg-surface shadow-lg">
                  {usersLoading && (
                    <p className="px-3 py-2 text-sm text-ink-muted">
                      {t("state.loading")}
                    </p>
                  )}
                  {!usersLoading && filteredUsers.length === 0 && (
                    <p className="px-3 py-2 text-sm text-ink-muted">
                      {t("userSessions.noMatch")}
                    </p>
                  )}
                  {!usersLoading &&
                    filteredUsers.slice(0, 50).map((u) => (
                      <button
                        type="button"
                        key={u.id}
                        onClick={() => {
                          setSelectedUserId(u.id);
                          setUserFilter("");
                          setShowDropdown(false);
                        }}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-surface-hover"
                      >
                        <span className="font-medium text-ink">
                          {u.full_name ??
                            u.email ??
                            u.code ??
                            t("userSessions.user")}
                        </span>
                        <span className="ml-2 text-ink-muted">
                          {u.email ?? t("userSessions.noEmail")}
                        </span>
                        {u.role && (
                          <span className="ml-2 text-xs text-ink-subtle">
                            {u.role}
                          </span>
                        )}
                      </button>
                    ))}
                  {!usersLoading && filteredUsers.length > 50 && (
                    <p className="px-3 py-2 text-xs text-ink-subtle">
                      {t("userSessions.showingLimited", {
                        shown: 50,
                        total: filteredUsers.length,
                      })}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Month filter */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-muted">
                {t("userSessions.month")}
              </label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-surface-muted dark:text-white"
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-500">{t(error)}</p>}

          <ActionButton
            type="submit"
            disabled={!selectedUserId || loading}
            className="w-fit"
          >
            {loading ? t("state.loading") : t("action.search")}
          </ActionButton>
        </form>
      </Card>

      {searched && (
        <Card extra="w-full p-5">
          <p className="mb-3 text-lg font-semibold text-ink">
            {t("userSessions.resultCount", { count: total })}
          </p>

          {sessions.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {t("userSessions.empty")}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">
                        {t("userSessions.colTitle")}
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">
                        {t("userSessions.colClass")}
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">
                        {t("userSessions.colStart")}
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">
                        {t("userSessions.colEnd")}
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">
                        {t("userSessions.colStatus")}
                      </th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">
                        {t("userSessions.colVenue")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s) => (
                      <tr
                        key={s.id}
                        className="border-b border-line"
                      >
                        <td className="px-3 py-2 text-ink">
                          {s.title}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {s.class_name || "-"}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {s.start_time
                            ? formatDateTime(s.start_time, locale)
                            : t("userSessions.unscheduled")}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {s.end_time
                            ? formatDateTime(s.end_time, locale)
                            : t("userSessions.unscheduled")}
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge
                            label={t(statusBadgeKey(s.status))}
                            tone={statusBadgeTone(s.status)}
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {s.location || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > PAGE_SIZE && (
                <div className="mt-4 flex items-center gap-3">
                  <ActionButton
                    variant="secondary"
                    size="sm"
                    disabled={skip === 0}
                    onClick={() => fetchSessions(skip - PAGE_SIZE)}
                  >
                    {t("notif.prev")}
                  </ActionButton>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} / {total}
                  </span>
                  <ActionButton
                    variant="secondary"
                    size="sm"
                    disabled={skip + PAGE_SIZE >= total}
                    onClick={() => fetchSessions(skip + PAGE_SIZE)}
                  >
                    {t("notif.next")}
                  </ActionButton>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      <ExportHistoryModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        selectedMonth={selectedMonth}
        students={allUsers}
      />
    </div>
  );
};

export default UserSessions;
